import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import EditarPeriodoDialog from './EditarPeriodoDialog';
import { usePeriodo, type PatronPeriodo } from '../../context/PeriodoContext';

interface PeriodosFormProps {
  onPeriodoHomeChange: (fechaInicio: string, fechaFin: string) => void;
  onPeriodoCallCenterChange: (fechaInicio: string, fechaFin: string) => void;
  onSaved?: () => void;
}

/**
 * Aplica un patrón de periodo a un mes/año específico
 * Ajusta automáticamente los días según la cantidad de días del mes
 */
const aplicarPatron = (patron: PatronPeriodo, mesBase: number, anioBase: number) => {
  // Calcular el mes y año de inicio
  const mesInicio = mesBase;
  const anioInicio = anioBase;

  // Calcular el mes y año de fin
  const mesFin = mesBase + patron.mesesOffset;
  const anioFin = anioBase;

  // Obtener el último día del mes de inicio
  const ultimoDiaMesInicio = new Date(anioInicio, mesInicio + 1, 0).getDate();
  // Ajustar el día de inicio si excede los días del mes
  const diaInicioAjustado = Math.min(patron.diaInicio, ultimoDiaMesInicio);

  // Obtener el último día del mes de fin
  const ultimoDiaMesFin = new Date(anioFin, mesFin + 1, 0).getDate();
  // Ajustar el día de fin si excede los días del mes
  const diaFinAjustado = Math.min(patron.diaFin, ultimoDiaMesFin);

  const fechaInicio = new Date(anioInicio, mesInicio, diaInicioAjustado);
  const fechaFin = new Date(anioFin, mesFin, diaFinAjustado);

  return {
    fecha_inicio: fechaInicio.toISOString().split('T')[0],
    fecha_fin: fechaFin.toISOString().split('T')[0],
  };
};

/**
 * Calcula el patrón a partir de dos fechas
 */
// const calcularPatronDesdeFechas = (fechaInicio: string, fechaFin: string): PatronPeriodo => {
//   const inicio = new Date(fechaInicio);
//   const fin = new Date(fechaFin);

//   const diaInicio = inicio.getDate();
//   const diaFin = fin.getDate();

//   // Calcular diferencia de meses
//   let mesesOffset = (fin.getFullYear() - inicio.getFullYear()) * 12;
//   mesesOffset += fin.getMonth() - inicio.getMonth();

//   return {
//     diaInicio,
//     diaFin,
//     mesesOffset
//   };
// };

/**
 * Genera los períodos HOME disponibles (del día 26 de un mes al 25 del siguiente)
 * Por defecto muestra el período anterior al actual
 */
const generarPeriodosHome = (patronPersonalizado?: PatronPeriodo | null) => {
  const periodos: Array<{ label: string; fecha_inicio: string; fecha_fin: string }> = [];
  const hoy = new Date();

  // Patrón por defecto: día 26 al 25 del mes siguiente
  const patronDefault: PatronPeriodo = { diaInicio: 26, diaFin: 25, mesesOffset: 1 };
  const patron = patronPersonalizado || patronDefault;

  // Generar los últimos 12 períodos
  for (let i = 1; i <= 12; i++) {
    // Mes base para calcular
    const mesBase = hoy.getMonth() - i;
    const anioBase = hoy.getFullYear();

    // Aplicar patrón
    const fechas = aplicarPatron(patron, mesBase, anioBase);

    // Calcular el nombre del mes del inicio del periodo
    const fechaInicioDate = new Date(fechas.fecha_inicio);
    const nombreMesInicio = fechaInicioDate.toLocaleDateString('es-ES', { month: 'long' });
    const nombreMesInicioCapitalizado = nombreMesInicio.charAt(0).toUpperCase() + nombreMesInicio.slice(1);
    const anioInicio = fechaInicioDate.getFullYear();

    const label = `${nombreMesInicioCapitalizado} ${anioInicio} (${fechas.fecha_inicio} al ${fechas.fecha_fin})`;

    periodos.push({
      label,
      fecha_inicio: fechas.fecha_inicio,
      fecha_fin: fechas.fecha_fin,
    });
  }

  return periodos;
};

/**
 * Genera los períodos CALL CENTER disponibles (del día 01 al último día del mes 30/31)
 * Por defecto muestra el período anterior al actual
 */
const generarPeriodosCallCenter = (patronPersonalizado?: PatronPeriodo | null) => {
  const periodos: Array<{ label: string; fecha_inicio: string; fecha_fin: string }> = [];
  const hoy = new Date();

  // Patrón por defecto: día 01 al último día del mismo mes
  const patronDefault: PatronPeriodo = { diaInicio: 1, diaFin: 31, mesesOffset: 0 };
  const patron = patronPersonalizado || patronDefault;

  // Generar los últimos 12 períodos
  for (let i = 1; i <= 12; i++) {
    // Mes base para calcular
    const mesBase = hoy.getMonth() - i;
    const anioBase = hoy.getFullYear();

    // Aplicar patrón (ya incluye ajuste automático de días)
    const fechas = aplicarPatron(patron, mesBase, anioBase);

    // Calcular el nombre del mes del inicio del periodo
    const fechaInicioDate = new Date(fechas.fecha_inicio);
    const nombreMesInicio = fechaInicioDate.toLocaleDateString('es-ES', { month: 'long' });
    const nombreMesInicioCapitalizado = nombreMesInicio.charAt(0).toUpperCase() + nombreMesInicio.slice(1);
    const anioInicio = fechaInicioDate.getFullYear();

    const label = `${nombreMesInicioCapitalizado} ${anioInicio} (${fechas.fecha_inicio} al ${fechas.fecha_fin})`;

    periodos.push({
      label,
      fecha_inicio: fechas.fecha_inicio,
      fecha_fin: fechas.fecha_fin,
    });
  }

  return periodos;
};

export default function PeriodosForm({ onPeriodoHomeChange, onPeriodoCallCenterChange, onSaved }: PeriodosFormProps) {
  const {
    setPeriodo,
    setPeriodoCallCenter,
    patronPersonalizadoHome,
    patronPersonalizadoCallCenter,
    guardarPatronHome,
    guardarPatronCallCenter,
    eliminarPatronHome,
    eliminarPatronCallCenter,
  } = usePeriodo();

  // Generar periodos con el patrón personalizado si existe
  const periodosHome = generarPeriodosHome(patronPersonalizadoHome);
  const periodosCallCenter = generarPeriodosCallCenter(patronPersonalizadoCallCenter);

  // Por defecto, seleccionar el período anterior (índice 0) para HOME
  const [periodoHomeSeleccionado, setPeriodoHomeSeleccionado] = useState(0);
  const [periodoCallCenterSeleccionado, setPeriodoCallCenterSeleccionado] = useState(0);

  // Diálogos de edición
  const [dialogHomeOpen, setDialogHomeOpen] = useState(false);
  const [dialogCallCenterOpen, setDialogCallCenterOpen] = useState(false);

  const periodoHomeActual = periodosHome[periodoHomeSeleccionado];
  const periodoCallCenterActual = periodosCallCenter[periodoCallCenterSeleccionado];

  // Notificar al padre cuando cambien los patrones o al montar
  useEffect(() => {
    const periodoHome = periodosHome[periodoHomeSeleccionado] || periodosHome[0];
    const periodoCallCenter = periodosCallCenter[periodoCallCenterSeleccionado] || periodosCallCenter[0];

    onPeriodoHomeChange(periodoHome.fecha_inicio, periodoHome.fecha_fin);
    onPeriodoCallCenterChange(periodoCallCenter.fecha_inicio, periodoCallCenter.fecha_fin);
    setPeriodo(periodoHome.fecha_inicio, periodoHome.fecha_fin);
    setPeriodoCallCenter(periodoCallCenter.fecha_inicio, periodoCallCenter.fecha_fin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patronPersonalizadoHome, patronPersonalizadoCallCenter]);

  const handleChangePeriodoHome = (event: SelectChangeEvent<number>) => {
    const index = Number(event.target.value);
    setPeriodoHomeSeleccionado(index);

    const periodo = periodosHome[index];
    onPeriodoHomeChange(periodo.fecha_inicio, periodo.fecha_fin);
    setPeriodo(periodo.fecha_inicio, periodo.fecha_fin);
  };

  const handleChangePeriodoCallCenter = (event: SelectChangeEvent<number>) => {
    const index = Number(event.target.value);
    setPeriodoCallCenterSeleccionado(index);

    const periodo = periodosCallCenter[index];
    onPeriodoCallCenterChange(periodo.fecha_inicio, periodo.fecha_fin);
    setPeriodoCallCenter(periodo.fecha_inicio, periodo.fecha_fin);
  };

  const handleGuardarPeriodoHome = (diaInicio: number, diaFin: number, mesesOffset: number) => {
    const patron: PatronPeriodo = { diaInicio, diaFin, mesesOffset };
    guardarPatronHome(patron);
    // El useEffect se encargará de actualizar las fechas cuando cambie patronPersonalizadoHome
    if (onSaved) {
      onSaved();
    }
  };

  const handleGuardarPeriodoCallCenter = (diaInicio: number, diaFin: number, mesesOffset: number) => {
    const patron: PatronPeriodo = { diaInicio, diaFin, mesesOffset };
    guardarPatronCallCenter(patron);
    // El useEffect se encargará de actualizar las fechas cuando cambie patronPersonalizadoCallCenter
    if (onSaved) {
      onSaved();
    }
  };

  const handleRestablecerHome = () => {
    eliminarPatronHome();
    // Después de eliminar el patrón, los periodos se regeneran automáticamente
    const periodosDefault = generarPeriodosHome(null);
    const periodoActual = periodosDefault[periodoHomeSeleccionado];
    onPeriodoHomeChange(periodoActual.fecha_inicio, periodoActual.fecha_fin);
    setPeriodo(periodoActual.fecha_inicio, periodoActual.fecha_fin);
    if (onSaved) {
      onSaved();
    }
  };

  const handleRestablecerCallCenter = () => {
    eliminarPatronCallCenter();
    // Después de eliminar el patrón, los periodos se regeneran automáticamente
    const periodosDefault = generarPeriodosCallCenter(null);
    const periodoActual = periodosDefault[periodoCallCenterSeleccionado];
    onPeriodoCallCenterChange(periodoActual.fecha_inicio, periodoActual.fecha_fin);
    setPeriodoCallCenter(periodoActual.fecha_inicio, periodoActual.fecha_fin);
    if (onSaved) {
      onSaved();
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Período de Cálculo
      </Typography>

      {/* Período HOME (26 al 25) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              📅 Período HOME
            </Typography>
            {patronPersonalizadoHome && (
              <Chip
                label="Personalizado"
                color="primary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {patronPersonalizadoHome ? (
                <>
                  Patrón personalizado: <strong>día {patronPersonalizadoHome.diaInicio}</strong> al{' '}
                  <strong>día {patronPersonalizadoHome.diaFin}</strong>
                  {patronPersonalizadoHome.mesesOffset > 0 && ' del mes siguiente'}.
                </>
              ) : (
                <>
                  Este período se calcula desde el <strong>día 26 de un mes</strong> hasta el{' '}
                  <strong>día 25 del mes siguiente</strong>. Se usa para canales de venta estándar.
                </>
              )}
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="periodo-home-label">Período HOME</InputLabel>
                <Select
                  labelId="periodo-home-label"
                  value={periodoHomeSeleccionado}
                  onChange={handleChangePeriodoHome}
                  label="Período HOME"
                >
                  {periodosHome.map((periodo, index) => (
                    <MenuItem key={index} value={index}>
                      {periodo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'success.light',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.main',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  📅 Período HOME Seleccionado:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Desde: <strong>{periodoHomeActual.fecha_inicio}</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Hasta: <strong>{periodoHomeActual.fecha_fin}</strong>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setDialogHomeOpen(true)}
                  size="small"
                >
                  Personalizar Patrón de Período
                </Button>
                {patronPersonalizadoHome && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={handleRestablecerHome}
                    size="small"
                  >
                    Restablecer a Predeterminado
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Período CALL CENTER (01 al 30/31) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'secondary.main' }}>
              📞 Período CALL CENTER
            </Typography>
            {patronPersonalizadoCallCenter && (
              <Chip
                label="Personalizado"
                color="secondary"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {patronPersonalizadoCallCenter ? (
                <>
                  Patrón personalizado: <strong>día {patronPersonalizadoCallCenter.diaInicio}</strong> al{' '}
                  <strong>día {patronPersonalizadoCallCenter.diaFin}</strong>
                  {patronPersonalizadoCallCenter.mesesOffset > 0 && ' del mes siguiente'}.
                </>
              ) : (
                <>
                  Este período se calcula desde el <strong>día 01</strong> hasta el{' '}
                  <strong>último día del mes (30/31)</strong>. Se usa para el canal <strong>"Vendedor Call Center"</strong>.
                </>
              )}
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="periodo-cc-label">Período Call Center</InputLabel>
                <Select
                  labelId="periodo-cc-label"
                  value={periodoCallCenterSeleccionado}
                  onChange={handleChangePeriodoCallCenter}
                  label="Período Call Center"
                >
                  {periodosCallCenter.map((periodo, index) => (
                    <MenuItem key={index} value={index}>
                      {periodo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'warning.light',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  📅 Período Call Center Seleccionado:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Desde: <strong>{periodoCallCenterActual.fecha_inicio}</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Hasta: <strong>{periodoCallCenterActual.fecha_fin}</strong>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setDialogCallCenterOpen(true)}
                  size="small"
                >
                  Personalizar Patrón de Período
                </Button>
                {patronPersonalizadoCallCenter && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={handleRestablecerCallCenter}
                    size="small"
                  >
                    Restablecer a Predeterminado
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Diálogos de edición */}
      <EditarPeriodoDialog
        open={dialogHomeOpen}
        onClose={() => setDialogHomeOpen(false)}
        onGuardar={handleGuardarPeriodoHome}
        tipoPeriodo="HOME"
        fechaInicioActual={periodoHomeActual.fecha_inicio}
        fechaFinActual={periodoHomeActual.fecha_fin}
      />

      <EditarPeriodoDialog
        open={dialogCallCenterOpen}
        onClose={() => setDialogCallCenterOpen(false)}
        onGuardar={handleGuardarPeriodoCallCenter}
        tipoPeriodo="CALL_CENTER"
        fechaInicioActual={periodoCallCenterActual.fecha_inicio}
        fechaFinActual={periodoCallCenterActual.fecha_fin}
      />
    </Box>
  );
}
